import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ADMIN_EMAIL } from './config/constants';
import HomePage from './pages/HomePage';
import AboutUs from './pages/AboutUs';
import InterestForm from './pages/InterestForm';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ClinicDashboard from './pages/ClinicDashboard';
import TechnicianDashboard from './pages/TechnicianDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminReset from './pages/AdminReset';
import ShiftForm from './pages/ShiftForm';
import PlaceholderPage from './pages/PlaceholderPage';
import ForClinics from './pages/ForClinics';
import ForTechnicians from './pages/ForTechnicians';
import AIPowered from './pages/AIPowered';
import InnovationRoadmap from './pages/InnovationRoadmap';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Gracias from './pages/Gracias';
import './App.css';

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Admin-only Protected Route
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Check if user is admin
  if (user && user.email === ADMIN_EMAIL) {
    return children;
  }
  
  // Redirect to interest form for non-admin users
  return <Navigate to="/interes" state={{ from: location }} replace />;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/interes" element={<InterestForm />} />
      <Route path="/interest" element={<InterestForm />} />
      <Route path="/interesse" element={<InterestForm />} />
      <Route path="/for-clinics" element={<ForClinics />} />
      <Route path="/for-technicians" element={<ForTechnicians />} />
      <Route path="/ai-powered" element={<AIPowered />} />
      <Route path="/innovacion-y-hoja-de-ruta" element={<InnovationRoadmap />} />
      <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
      <Route path="/gracias" element={<Gracias />} />

      {/* Obfuscated Admin Login */}
      <Route path="/liberado-jefe-login" element={<AdminLogin />} />
      <Route path="/liberado-jefe-reset" element={<AdminReset />} />

      {/* Redirects for old/deprecated routes */}
      <Route path="/admin-login" element={<Navigate to="/interes" replace />} />
      <Route path="/admin-reset" element={<Navigate to="/interes" replace />} />
      <Route path="/admin" element={<Navigate to="/interes" replace />} />
      <Route path="/admin/*" element={<Navigate to="/interes" replace />} />
      <Route path="/clinic" element={<Navigate to="/interes" replace />} />
      <Route path="/clinic/*" element={<Navigate to="/interes" replace />} />
      <Route path="/technician" element={<Navigate to="/interes" replace />} />
      <Route path="/tech" element={<Navigate to="/interes" replace />} />
      <Route path="/tech/*" element={<Navigate to="/interes" replace />} />
      <Route path="/dashboard" element={<Navigate to="/interes" replace />} />
      <Route path="/test-mode" element={<Navigate to="/interes" replace />} />
      <Route path="/login" element={<Navigate to="/interes" replace />} />
      <Route path="/signup" element={<Navigate to="/interes" replace />} />

      {/* Protected Dashboards - Admin Only (Obfuscated) */}
      <Route
        path="/liberado-jefe-clinic/dashboard"
        element={
          <AdminRoute>
            <ClinicDashboard />
          </AdminRoute>
        }
      />
      
      <Route
        path="/liberado-jefe-clinic/post-shift"
        element={
          <AdminRoute>
            <ShiftForm />
          </AdminRoute>
        }
      />

      <Route
        path="/liberado-jefe-tech/dashboard"
        element={
          <AdminRoute>
            <TechnicianDashboard />
          </AdminRoute>
        }
      />

      <Route
        path="/liberado-jefe/dashboard"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />

      {/* Placeholder Routes - Admin Only */}
      <Route
        path="/placeholder/:feature"
        element={
          <AdminRoute>
            <PlaceholderPage 
              title="Feature Coming Soon" 
              description="This feature will be implemented in Phase 2+"
              backTo="-1"
            />
          </AdminRoute>
        }
      />

      {/* Default Route */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <AppRoutes />
          </SubscriptionProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
